import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { X, Download, Calendar, FileText, Building2, User } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import companyStore from '../../store/company-store';
import { t } from 'i18next';
import CompactDatePicker from '../ui/compact-date-picker';

interface ExportRevenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (
    startDate: string,
    endDate: string,
    format: 'csv' | 'pdf',
    collaboratorId?: string
  ) => Promise<boolean>;
  defaultStartDate?: string;
  defaultEndDate?: string;
}

const ExportRevenueModal: React.FC<ExportRevenueModalProps> = observer(({
  isOpen,
  onClose,
  onExport,
  defaultStartDate,
  defaultEndDate
}) => {
  // États locaux
  const [startDate, setStartDate] = useState(defaultStartDate || format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(defaultEndDate || format(new Date(), 'yyyy-MM-dd'));
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [revenueType, setRevenueType] = useState<'company' | 'collaborator'>('company');
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  // Récupérer les collaborateurs depuis le store
  const collaborators = companyStore.selectedCompany?.collaboratorCompanies || [];

  // Gérer la soumission
  const handleSubmit = async () => {
    setIsExporting(true);

    try {
      const collaboratorId = revenueType === 'collaborator' ? selectedCollaboratorId : undefined;
      const success = await onExport(startDate, endDate, exportFormat, collaboratorId);

      if (success) {
        onClose();
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Validation du formulaire
  const isFormValid = () => {
    if (!startDate || !endDate) return false;
    if (revenueType === 'collaborator' && !selectedCollaboratorId) return false;
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
              <Download className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('reservations.export.title', 'Exporter le chiffre d\'affaires')}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {/* Sélection du format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <FileText className="inline h-4 w-4 mr-1" />
              {t('reservations.export.format', 'Format d\'export')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setExportFormat('csv')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  exportFormat === 'csv'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="font-medium">CSV</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Excel compatible
                </div>
              </button>
              <button
                type="button"
                onClick={() => setExportFormat('pdf')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  exportFormat === 'pdf'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="font-medium">PDF</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Prêt à imprimer
                </div>
              </button>
            </div>
          </div>

          {/* Sélection des dates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <Calendar className="inline h-4 w-4 mr-1" />
              {t('reservations.export.period', 'Période')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <CompactDatePicker
                label={t('reservations.export.startDate', 'Date de début') || 'Date de début'}
                id="export-start-date"
                name="export-start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <CompactDatePicker
                label={t('reservations.export.endDate', 'Date de fin') || 'Date de fin'}
                id="export-end-date"
                name="export-end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
          </div>

          {/* Type de CA */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('reservations.export.revenueType', 'Type de chiffre d\'affaires')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setRevenueType('company');
                  setSelectedCollaboratorId('');
                }}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  revenueType === 'company'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Building2 className="h-5 w-5 mx-auto mb-1" />
                <div className="text-sm font-medium">
                  {t('reservations.export.company', 'Entreprise')}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  CA total
                </div>
              </button>
              <button
                type="button"
                onClick={() => setRevenueType('collaborator')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  revenueType === 'collaborator'
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <User className="h-5 w-5 mx-auto mb-1" />
                <div className="text-sm font-medium">
                  {t('reservations.export.collaborator', 'Collaborateur')}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  CA individuel
                </div>
              </button>
            </div>
          </div>

          {/* Sélection du collaborateur (conditionnel) */}
          {revenueType === 'collaborator' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="inline h-4 w-4 mr-1" />
                {t('reservations.export.selectCollaborator', 'Sélectionner un collaborateur')}
              </label>
              <select
                value={selectedCollaboratorId}
                onChange={(e) => setSelectedCollaboratorId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">
                  {t('reservations.export.chooseCollaborator', 'Choisir un collaborateur...')}
                </option>
                {collaborators.map((collaborator) => (
                  <option key={collaborator.collaboratorCompagnieId} value={collaborator.collaboratorCompagnieId}>
                    {collaborator.firstName} {collaborator.lastName} ({collaborator.role})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid() || isExporting}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Download size={18} />
            {isExporting
              ? t('reservations.export.exporting', 'Export en cours...')
              : t('reservations.export.export', 'Exporter')}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ExportRevenueModal;
